/**
 * Sprint 5-a · PlanPreview · 告知計畫（steps + 預估 tokens）→ 確認製作。
 * 「開始執行」button 明確標示：啟動 API 呼叫 · 開始計費 · 預估 tokens（費用透明度）。
 */
import React from 'react';
import { Box, Button, Chip, List, ListItem, ListItemText, Paper, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from 'react-i18next';

import { PlanStep } from '../services/aiOfficeService';

interface Props {
  steps: PlanStep[];
  estimatedTokens: number;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}

const PlanPreview: React.FC<Props> = ({ steps, estimatedTokens, onConfirm, onCancel, busy }) => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`agentic.${k}`, d);
  return (
    <Paper variant="outlined" sx={{ p: 1.25, mb: 1, bgcolor: '#f7fbff' }}>
      <Typography variant="subtitle2" gutterBottom>
        📋 {tt('planTitle', '執行計畫')}（{steps.length} {tt('steps', '步驟')}）
      </Typography>
      <List dense disablePadding>
        {steps.map((s) => (
          <ListItem key={s.index} disableGutters sx={{ alignItems: 'flex-start' }}>
            <ListItemText
              secondaryTypographyProps={{ component: 'div' }}
              primary={<Typography variant="body2" component="div"><strong>{s.index + 1}.</strong> {s.title}</Typography>}
              secondary={
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                  <Chip size="small" variant="outlined" label={`~${s.estimated_tokens} tok`} sx={{ height: 18 }} />
                </Stack>
              }
            />
          </ListItem>
        ))}
      </List>
      <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'stretch' }}>
        <Button variant="contained" size="small" startIcon={<PlayArrowIcon />}
          onClick={onConfirm} disabled={busy} sx={{ flexDirection: 'column', py: 0.75 }}>
          {tt('startExec', '開始執行')}
          <Typography variant="caption" component="span" sx={{ opacity: 0.85, lineHeight: 1.2 }}>
            {tt('startExecHint', '啟動 API · 開始計費')} · ~{estimatedTokens.toLocaleString()} tok
          </Typography>
        </Button>
        <Button variant="text" color="inherit" size="small" onClick={onCancel} disabled={busy}>
          {tt('cancelPlan', '取消')}
        </Button>
      </Box>
    </Paper>
  );
};

export default PlanPreview;
